import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, FileText, History, Trash2, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * HistoricalImportTab
 * -------------------
 * Upload pre-Investec-sync bank statements as CSV. Each row is committed as
 * a bank-side transaction (`source: investec_historical`) and immediately
 * available on the reconciliation tab for matching against book entries.
 *
 * Required CSV columns (case-insensitive, spaces auto-stripped):
 *   • transaction_date | posting_date | date
 *   • amount (signed: + credit, − debit; accepts R / commas / parens)
 *   • description | reference | notes | narrative
 *
 * Optional: value_date, running_balance, investec_uuid.
 */
export function HistoricalImportTab({ API_URL, headers, mappings }) {
  const [file, setFile] = useState(null);
  const [accountId, setAccountId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchBatches = useCallback(async () => {
    setBatchesLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/investec/historical-batches`, { headers });
      setBatches(r.data.batches || []);
    } catch (err) {
      // Silent — endpoint may not be deployed yet
    } finally {
      setBatchesLoading(false);
    }
  }, [API_URL, headers]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const handleUpload = async () => {
    if (!file || !accountId) {
      toast.error('Pick a file AND an Investec account');
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('investec_account_id', accountId);
      const r = await axios.post(
        `${API_URL}/api/admin/investec/import-historical-csv`,
        fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      setResult(r.data);
      toast.success(r.data.message || 'Historical import complete');
      setFile(null);
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteBatch = async (batch) => {
    const msg = `Delete historical bank batch "${batch.batch_name || batch.batch_id}"?\n\n` +
                `• ${batch.row_count} bank transactions\n` +
                `• Investor: ${batch.investor_name || '—'}\n` +
                `• Date range: ${batch.min_date?.slice(0,10)} → ${batch.max_date?.slice(0,10)}\n\n` +
                `Any allocations referencing these bank rows will also be removed. ` +
                `This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setDeletingId(batch.batch_id);
    try {
      const r = await axios.delete(
        `${API_URL}/api/admin/investec/historical-batches/${encodeURIComponent(batch.batch_id)}`,
        { headers }
      );
      toast.success(r.data.message || 'Deleted');
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card data-testid="historical-import-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Upload className="h-4 w-4" /> Upload Historical Bank Statement
          </CardTitle>
          <CardDescription>
            For periods that pre-date the live Investec sync. Rows are committed straight to
            the bank side of reconciliation and available for matching immediately.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-2 bg-rose-50 border border-rose-200 rounded text-xs flex items-start gap-2">
            <AlertCircle className="h-3 w-3 shrink-0 mt-0.5 text-rose-600" />
            <div className="space-y-1">
              <p className="font-semibold text-rose-900">Repair previously-uploaded historicals that leaked across investors</p>
              <p className="text-rose-800">
                Re-attaches the correct investor on every historical row via its account mapping.
                Run once after fixing any mappings that were missing an investor.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="border-rose-300 text-rose-700 hover:bg-rose-100 mt-1"
                onClick={async () => {
                  if (!window.confirm('Re-attach investor on every historical bank row?\nSafe to run repeatedly.')) return;
                  try {
                    const r = await axios.post(`${API_URL}/api/admin/investec/backfill-historical-investor`, {}, { headers });
                    toast.success(r.data.message || `Fixed ${r.data.fixed_count}`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Backfill failed');
                  }
                }}
                data-testid="historical-backfill-btn"
              >
                <RefreshCw className="h-3 w-3 mr-1" /> Backfill investor on historical rows
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Investec Account *</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger data-testid="historical-account-select">
                  <SelectValue placeholder="Select investor by RP code…" />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    // Sort by RP code so admins scanning for "01-00", "02-00" …
                    // find their investor quickly. Rows without an RP code
                    // (main disbursement, reserves, operating, etc.) sink
                    // to the bottom.
                    const rpKey = (m) => {
                      const rp = (m.risk_participant_code || '').trim();
                      return rp || '~' + (m.purpose || 'zzz');
                    };
                    const sorted = [...mappings].sort((a, b) => rpKey(a).localeCompare(rpKey(b), undefined, { numeric: true }));
                    return sorted.map(m => {
                      const key = m.investec_account_id || m.account_id;
                      const rp = m.risk_participant_code;
                      // Label the user actually asked for: RP code first,
                      // huge and monospaced. The bank account number and
                      // investor name are demoted to secondary text.
                      return (
                        <SelectItem key={key} value={key} data-testid={`historical-account-option-${rp || key}`}>
                          <div className="flex items-center gap-2 min-w-0 w-full">
                            <span className={`font-mono font-semibold text-sm ${rp ? 'text-slate-900' : 'text-slate-400 italic'}`}>
                              {rp || `no RP · ${m.purpose || 'unmapped'}`}
                            </span>
                            {rp && m.investor_name && (
                              <span className="text-[10px] text-slate-400 truncate">— {m.investor_name}</span>
                            )}
                          </div>
                        </SelectItem>
                      );
                    });
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">CSV File *</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={e => setFile(e.target.files?.[0] || null)}
                data-testid="historical-file-input"
              />
              {file && <p className="text-[10px] text-slate-500 truncate">{file.name}</p>}
            </div>
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900 space-y-1">
            <p className="font-semibold flex items-center gap-1"><FileText className="h-3 w-3" /> Required CSV columns:</p>
            <p>• <code className="bg-white px-1 rounded">transaction_date</code> (or <code>posting_date</code>, <code>date</code>)</p>
            <p>• <code className="bg-white px-1 rounded">amount</code> — signed; positive = credit, negative = debit. Accepts <code>R</code>, commas, parens.</p>
            <p>• <code className="bg-white px-1 rounded">description</code> (or <code>reference</code>, <code>notes</code>, <code>narrative</code>)</p>
            <p className="mt-2 font-semibold">Optional:</p>
            <p>• <code className="bg-white px-1 rounded">value_date</code>, <code className="bg-white px-1 rounded">running_balance</code>, <code className="bg-white px-1 rounded">investec_uuid</code></p>
            <p className="mt-2 text-blue-700"><strong>Deduplication:</strong> rows matching <code>(account, date, amount, description)</code> are auto-skipped, and rows with a known <code>investec_uuid</code> won't duplicate live-synced txns.</p>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleUpload}
              disabled={!file || !accountId || uploading}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="historical-upload-btn"
            >
              {uploading ? 'Importing…' : 'Upload & Import'}
            </Button>
          </div>

          {result && (
            <div className={`p-3 rounded border ${result.errors_count > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'} space-y-2 text-sm`} data-testid="historical-result">
              <p className="font-semibold flex items-center gap-1">
                {result.errors_count > 0
                  ? <AlertCircle className="h-4 w-4 text-amber-600" />
                  : <CheckCircle className="h-4 w-4 text-emerald-600" />}
                {result.message}
              </p>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="p-2 bg-white border rounded">
                  <p className="text-xl font-bold text-emerald-700">{result.imported_count}</p>
                  <p className="text-slate-500">Imported</p>
                </div>
                <div className="p-2 bg-white border rounded">
                  <p className="text-xl font-bold text-slate-700">{result.skipped_duplicates}</p>
                  <p className="text-slate-500">Duplicates skipped</p>
                </div>
                <div className="p-2 bg-white border rounded">
                  <p className="text-xl font-bold text-rose-700">{result.errors_count}</p>
                  <p className="text-slate-500">Row errors</p>
                </div>
              </div>
              {(result.errors || []).length > 0 && (
                <div className="text-xs space-y-0.5">
                  <p className="font-semibold text-amber-900">Errors (first 10):</p>
                  {result.errors.slice(0, 10).map((e, i) => (
                    <p key={i} className="text-amber-800">Row {e.row}: {e.error}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-4 w-4" /> Historical Import History
              </CardTitle>
              <CardDescription>
                Every historical CSV upload. Delete a batch to surgically undo.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchBatches} disabled={batchesLoading}>
              <RefreshCw className={`h-3 w-3 mr-1 ${batchesLoading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {batches.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-8">No historical uploads yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Investor</TableHead>
                  <TableHead className="text-right">Rows</TableHead>
                  <TableHead>Date range</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((b, i) => (
                  <TableRow key={b.batch_id} data-testid={`historical-batch-row-${i}`}>
                    <TableCell className="text-xs">
                      <div className="truncate max-w-[180px]">{b.batch_name || <span className="italic text-slate-400">no filename</span>}</div>
                      <div className="text-[10px] text-slate-400 font-mono truncate max-w-[180px]">{b.batch_id?.slice(0, 8)}…</div>
                    </TableCell>
                    <TableCell className="text-xs font-mono">{b.account_id?.slice(0, 10)}…</TableCell>
                    <TableCell className="text-xs truncate max-w-[160px]">{b.investor_name || '—'}</TableCell>
                    <TableCell className="text-right"><Badge variant="outline">{b.row_count}</Badge></TableCell>
                    <TableCell className="text-[10px] text-slate-600">{b.min_date?.slice(0,10)} → {b.max_date?.slice(0,10)}</TableCell>
                    <TableCell className="text-[10px] text-slate-600">{b.uploaded_at ? new Date(b.uploaded_at).toLocaleString('en-ZA') : '—'}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteBatch(b)}
                        disabled={deletingId === b.batch_id}
                        data-testid={`historical-batch-delete-${i}`}
                      >
                        {deletingId === b.batch_id ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default HistoricalImportTab;
